import type { PermisoRbac, RolRbac } from '../models/rbac.model';

export const ROL_VENDEDOR_NOMBRE = 'Vendedor';
export const ROL_VENDEDOR_DESCRIPCION =
  'Originación, cartera propia, ruta de cobros, pagos en campo/caja y colocación del mes.';

/** Par módulo+acción del perfil recomendado para vendedores de campo. */
export type PermisoMinimoVendedor = { readonly modulo: string; readonly accion: string };

/**
 * Permisos sugeridos al configurar el rol «Vendedor» (botón en Roles y permisos).
 * Alineado a: clientes, créditos, simulador, ruta mi-ruta, reporte-cobro, caja y control de colocación.
 */
export const PERMISOS_MINIMOS_VENDEDOR: readonly PermisoMinimoVendedor[] = [
  { modulo: 'CLIENTES', accion: 'VER' },
  { modulo: 'CLIENTES', accion: 'CREAR' },
  { modulo: 'CREDITOS', accion: 'VER' },
  { modulo: 'CREDITOS', accion: 'CREAR' },
  { modulo: 'CREDITOS', accion: 'PAGAR' },
  { modulo: 'COBROS', accion: 'VER' },
  { modulo: 'COBROS', accion: 'REGISTRAR_COMUNICACION' },
  { modulo: 'CAJA', accion: 'VER' },
  { modulo: 'CAJA', accion: 'GESTIONAR' },
  { modulo: 'REPORTES', accion: 'VER' },
] as const;

export const ETIQUETAS_PERMISOS_SUGERIDOS_VENDEDOR: Readonly<Record<string, string>> = {
  'CLIENTES:VER': 'Ver directorio de clientes',
  'CLIENTES:CREAR': 'Alta de clientes y expediente',
  'CREDITOS:VER': 'Mis créditos, cuotas y fichas',
  'CREDITOS:CREAR': 'Solicitar crédito (pendiente de aprobación) y simulador',
  'CREDITOS:PAGAR': 'Registrar pagos (caja y complemento a cobro en ruta)',
  'COBROS:VER': 'Ruta del día, cartera, reporte de cobro y corte',
  'COBROS:REGISTRAR_COMUNICACION': 'Registrar gestión / llamadas de cobranza',
  'CAJA:VER': 'Ver cajas asignadas',
  'CAJA:GESTIONAR': 'Abrir turno y terminal de cobro en oficina',
  'REPORTES:VER': 'Control de colocación y reportes propios del mes',
};

function clavePermiso(modulo: string, accion: string): string {
  return `${modulo.trim().toUpperCase()}:${accion.trim().toUpperCase()}`;
}

export function permisoEnCatalogoApi(
  permisos: readonly PermisoRbac[],
  modulo: string,
  accion: string,
): PermisoRbac | undefined {
  const clave = clavePermiso(modulo, accion);
  return permisos.find((p) => clavePermiso(p.modulo, p.accion) === clave);
}

/** Permisos sugeridos que el API aún no devolvió en `GET /permisos`. */
export function permisosSugeridosVendedorFaltantesEnApi(
  permisos: readonly PermisoRbac[],
): PermisoMinimoVendedor[] {
  return PERMISOS_MINIMOS_VENDEDOR.filter((s) => !permisoEnCatalogoApi(permisos, s.modulo, s.accion));
}

export function esNombreRolVendedor(nombre: string | null | undefined): boolean {
  return String(nombre ?? '')
    .trim()
    .toLowerCase() === 'vendedor';
}

export function buscarRolVendedor(roles: readonly RolRbac[]): RolRbac | null {
  return roles.find((r) => esNombreRolVendedor(r.nombre)) ?? null;
}

/**
 * Sesión con rol «Vendedor».
 * Prioriza el nombre del rol (auth/me) porque el vendedor no suele poder listar roles vía RBAC.
 */
export function esRolVendedorSesion(
  rolIdUsuario: number | null | undefined,
  rolVendedorId: number | null | undefined,
  rolNombreUsuario?: string | null,
): boolean {
  if (esNombreRolVendedor(rolNombreUsuario)) return true;
  return (
    rolIdUsuario != null &&
    rolVendedorId != null &&
    Number.isFinite(rolIdUsuario) &&
    Number.isFinite(rolVendedorId) &&
    Math.trunc(rolIdUsuario) === Math.trunc(rolVendedorId)
  );
}

export function idsPermisosSugeridosVendedor(permisos: readonly PermisoRbac[]): number[] {
  return PERMISOS_MINIMOS_VENDEDOR.map((s) => permisoEnCatalogoApi(permisos, s.modulo, s.accion)?.id)
    .filter((id): id is number => id != null && id > 0)
    .sort((a, b) => a - b);
}

export function fusionarIdsPermisos(base: readonly number[], extra: readonly number[]): number[] {
  return [...new Set([...base, ...extra])].sort((a, b) => a - b);
}
